import styled, { keyframes } from "styled-components";

import {
  ANIMATION,
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
} from "../../../../../utils/constants";
import { toneColor } from "../../../../../utils";
import { ReturnTone } from "./types";
import { BaseButton } from "../../../../../components/Button";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Bars grow from their anchored edge on entrance via a composited transform,
// never an animated width (layout property). Ends at scaleX(1) so the pill caps
// rest undistorted.
const growX = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

export const Panel = styled.section`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: ${SPACING_RESPONSIVE.mobile.pageMargin}px;
  margin-bottom: 0px;
  border-radius: ${SPACING.md}px;
  background: var(--color-card-background);
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-small);
  animation: ${fadeIn} ${ANIMATION.duration.moderate}
    ${ANIMATION.easing.easeOut};

  @media ${QUERIES.tabletAndUp} {
    margin: ${SPACING_RESPONSIVE.tablet.pageMargin}px;
    margin-bottom: 0px;
  }

  @media ${QUERIES.tabletLargeAndUp} {
    margin: ${SPACING_RESPONSIVE.desktop.pageMargin}px;
    margin-bottom: 0px;
  }
`;

// The header is a row, not one big button: the collapse affordance lives in
// CollapseButton (chevron + title) while the breakdown control sits opposite it,
// so two interactive controls are never nested. It wraps on narrow screens.
export const Header = styled.header`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACING.sm}px;
  padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;
  border-bottom: 1px solid var(--color-border-light);
  background: var(--color-tab-background);
  color: var(--color-text-default);

  @media ${QUERIES.tabletLargeAndUp} {
    padding: ${SPACING.md}px ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  }
`;

export const CollapseButton = styled(BaseButton)`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  flex: 1 1 auto;
  min-width: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  text-align: left;
  transition: ${ANIMATION.transitions.all};
`;

export const Chevron = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  align-items: center;
  color: var(--color-text-secondary);
  transition: transform ${ANIMATION.duration.fast} ${ANIMATION.easing.easeOut};
  transform: rotate(${({ $expanded }) => ($expanded ? "0deg" : "180deg")});

  & > svg {
    font-size: var(--text-xl);
  }
`;

export const HeaderTitle = styled.span`
  font-size: var(--text-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-default);
`;

// Holding count, right-aligned. Shown while collapsed as a preview of what the
// panel holds; the breakdown control takes this slot once expanded.
export const HeaderSubtitle = styled.span`
  margin-left: auto;
  font-size: var(--text-base);
  color: var(--color-text-secondary);
`;

// Holds the breakdown SegmentedControl. Right-aligned in the header on desktop;
// on mobile it drops onto its own line and centres, matching how the other cards
// (LiabilityTrends, PerformanceChart) place their segmented controls.
export const HeaderControl = styled.div`
  display: flex;
  flex-basis: 100%;
  justify-content: center;

  @media ${QUERIES.tabletAndUp} {
    flex-basis: auto;
    margin-left: auto;
  }
`;

export const Body = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${SPACING.xl}px;
  padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;
  animation: ${fadeIn} ${ANIMATION.duration.fast} ${ANIMATION.easing.easeOut};

  @media ${QUERIES.tabletAndUp} {
    padding: ${SPACING_RESPONSIVE.tablet.cardPadding}px;
  }

  @media ${QUERIES.laptopAndUp} {
    grid-template-columns: 1fr 1fr;
    gap: ${SPACING.xxl}px;
    padding: ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  }
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  min-width: 0;
`;

export const ColumnHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
`;

export const ColumnTitle = styled.h3`
  margin: 0;
  font-size: var(--text-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-default);
`;

export const ColumnSubtitle = styled.span`
  align-self: flex-start;
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

// Shown when there is nothing to break down (holdings exist but their combined
// current value is zero, so there are no shares to allocate). Restrained and
// inline rather than the app's floating hero empty state: this is a rare
// degenerate case inside a compact panel, not a first-run activation moment.
export const EmptyBody = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACING.xs}px;
  padding: ${SPACING.xl}px ${SPACING.md}px;
  text-align: center;
`;

export const EmptyTitle = styled.p`
  margin: 0;
  font-size: var(--text-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-default);
`;

export const EmptyText = styled.p`
  margin: 0;
  max-width: 44ch;
  font-size: var(--text-sm);
  line-height: 1.6;
  color: var(--color-text-secondary);
`;

export const RowList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const Row = styled.li`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    "name value"
    "track track";
  align-items: center;
  gap: ${SPACING.xs}px ${SPACING.sm}px;

  @media ${QUERIES.tabletLargeAndUp} {
    grid-template-columns: 150px minmax(0, 1fr) 72px;
    grid-template-areas: "name track value";
    gap: ${SPACING.md}px;
  }
`;

export const Name = styled.span`
  grid-area: name;
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  min-width: 0;
  font-size: var(--text-base);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-default);
`;

export const NameText = styled.span`
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Swatch = styled.span<{ $color: string }>`
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${({ $color }) => $color};
`;

// A muted row has no computable return, so its dash reads as absent data rather
// than a real neutral figure. Otherwise tone by sign, or the default text colour
// for the untoned allocation percentages.
const resolveValueColor = (tone?: ReturnTone, muted?: boolean): string => {
  if (muted) {
    return "var(--color-text-secondary)";
  }
  if (tone) {
    return toneColor(tone);
  }
  return "var(--color-text-default)";
};

export const Value = styled.span<{ $tone?: ReturnTone; $muted?: boolean }>`
  grid-area: value;
  justify-self: end;
  font-size: var(--text-base);
  font-weight: var(--font-weight-bold);
  white-space: nowrap;
  color: ${({ $tone, $muted }) => resolveValueColor($tone, $muted)};
`;

export const Track = styled.div`
  grid-area: track;
  position: relative;
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: var(--color-background-3);
  overflow: hidden;
`;

// The returns track carries a faint zero baseline at its centre, so the diverging
// ReturnFill reads against a fixed reference: left of the line is a loss, right is
// a gain. The fill is a later-painted child, so it covers the line on its own
// side and the baseline stays visible on the empty side and at zero.
export const ReturnTrack = styled(Track)`
  &::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 1px;
    transform: translateX(-50%);
    background: var(--color-text-muted);
  }
`;

export const AllocationFill = styled.div<{ $width: number; $color: string }>`
  height: 100%;
  border-radius: 999px;
  width: ${({ $width }) => `${$width}%`};
  background: ${({ $color }) => $color};
  transform-origin: left center;
  animation: ${growX} ${ANIMATION.duration.moderate} ${ANIMATION.easing.easeOut}
    both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const ReturnFill = styled.div<{ $width: number; $tone: ReturnTone }>`
  position: absolute;
  top: 0;
  height: 100%;
  border-radius: 999px;
  width: ${({ $width }) => `${$width}%`};
  background: ${({ $tone }) => toneColor($tone)};
  ${({ $tone }) => ($tone === "negative" ? "right: 50%;" : "left: 50%;")}
  transform-origin: ${({ $tone }) =>
    $tone === "negative" ? "right center" : "left center"};
  animation: ${growX} ${ANIMATION.duration.moderate} ${ANIMATION.easing.easeOut}
    both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
